import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi } from "@/lib/api";
import {
  Link2, Users, ShieldCheck, Lock, AlertTriangle, RefreshCw, ScrollText, FlaskConical, Trash2, Settings2,
} from "lucide-react";

const STATUS_META = {
  self_declared: { label: "Self-Declared", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  awaiting_witness: { label: "Awaiting Witness", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  witnessed: { label: "Witnessed", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  mutually_witnessed: { label: "Mutually Witnessed", cls: "bg-blue-50 text-blue-800 border-blue-300" },
  witness_required_again: { label: "Witness Required Again", cls: "bg-rose-50 text-rose-800 border-rose-300" },
};

const Tile = ({ label, value, testId, icon: Icon, accent }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        {Icon && <Icon className={`h-4 w-4 ${accent || "text-primary"}`} />}
        <p className="text-[11px] font-semibold uppercase tracking-wider">{label}</p>
      </div>
      <p className="font-heading font-bold text-2xl tabular-nums mt-1.5" data-testid={testId}>{value}</p>
    </CardContent>
  </Card>
);

const AuditDialog = ({ declaration }) => {
  const [events, setEvents] = useState(null);
  const load = () => {
    adminApi.get(`/admin/witness/declarations/${declaration.id}/audit`)
      .then((r) => setEvents(r.data)).catch(() => toast.error("Could not load audit trail"));
  };
  return (
    <Dialog onOpenChange={(open) => open && load()}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`audit-btn-${declaration.declaration_number}`}>
          <ScrollText className="h-3.5 w-3.5 mr-1" /> Audit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Audit trail — {declaration.declaration_number}</DialogTitle>
          <DialogDescription>Append-only declaration events (oldest first).</DialogDescription>
        </DialogHeader>
        {!events && <Skeleton className="h-40" />}
        {events && (
          <ol className="space-y-2" data-testid="audit-event-list">
            {events.map((ev) => (
              <li key={ev.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-semibold text-foreground">{ev.event}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{ev.at}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">actor: {ev.actor}</p>
                {ev.meta && Object.keys(ev.meta).length > 0 && (
                  <pre className="text-[10px] bg-slate-50 border border-slate-100 rounded p-2 mt-1.5 overflow-x-auto">{JSON.stringify(ev.meta, null, 1)}</pre>
                )}
              </li>
            ))}
            {events.length === 0 && <p className="text-sm text-muted-foreground">No events recorded.</p>}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminWitness() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [flags, setFlags] = useState(null);
  const [testData, setTestData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [settings, setSettings] = useState({ required_witness_count: "", aggregate_min_threshold: "" });

  const load = useCallback(() => {
    adminApi.get("/admin/witness/summary").then((r) => {
      setSummary(r.data);
      setSettings({
        required_witness_count: String(r.data.settings.required_witness_count),
        aggregate_min_threshold: String(r.data.settings.aggregate_min_threshold),
      });
    }).catch(() => toast.error("Could not load witness summary"));
    adminApi.get("/admin/witness/flags").then((r) => setFlags(r.data)).catch(() => {});
    adminApi.get("/admin/witness/test-data").then((r) => setTestData(r.data)).catch(() => {});
  }, []);

  const loadRows = useCallback(() => {
    const params = {};
    if (statusFilter !== "all") params.witness_status = statusFilter;
    adminApi.get("/admin/witness/declarations", { params })
      .then((r) => setRows(r.data)).catch(() => toast.error("Could not load declarations"));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const saveSettings = async () => {
    try {
      await adminApi.patch("/admin/witness/settings", {
        required_witness_count: parseInt(settings.required_witness_count, 10),
        aggregate_min_threshold: parseInt(settings.aggregate_min_threshold, 10),
      });
      toast.success("Witness settings saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings");
    }
  };

  const purgeTestData = async () => {
    try {
      const r = await adminApi.delete("/admin/witness/test-data");
      toast.success(`Acceptance test data removed (${r.data.removed.declarations} declarations)`);
      load();
      loadRows();
    } catch {
      toast.error("Could not remove test data");
    }
  };

  const bs = summary?.by_status || {};

  return (
    <div className="max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="admin-witness-title">Mutual Witness</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Witness levels, coverage, review signals and audit trails. Signals are for review only — never proof of fraud.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { load(); loadRows(); }} data-testid="witness-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {!summary ? (
        <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-3"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <Tile label="Total declarations" value={summary.total_declarations} testId="tile-total" icon={Users} />
          <Tile label="Self-declared" value={bs.self_declared ?? 0} testId="tile-self" icon={ShieldCheck} accent="text-slate-500" />
          <Tile label="Awaiting witness" value={bs.awaiting_witness ?? 0} testId="tile-awaiting" icon={ShieldCheck} accent="text-amber-600" />
          <Tile label="Witnessed" value={bs.witnessed ?? 0} testId="tile-witnessed" icon={ShieldCheck} accent="text-emerald-600" />
          <Tile label="Mutually witnessed" value={bs.mutually_witnessed ?? 0} testId="tile-mutual" icon={Link2} accent="text-blue-600" />
          <Tile label="Witness required again" value={bs.witness_required_again ?? 0} testId="tile-required-again" icon={AlertTriangle} accent="text-rose-600" />
          <Tile label="Witness coverage" value={`${summary.witness_coverage_percent}%`} testId="tile-coverage" icon={ShieldCheck} accent="text-emerald-600" />
          <Tile label="2+ witnesses" value={summary.second_witness_records} testId="tile-second" icon={Users} />
          <Tile label="Changed after witnessing" value={summary.changed_after_witnessing} testId="tile-changed" icon={ScrollText} accent="text-amber-600" />
          <Tile label="Invalidated / revoked" value={summary.revoked_or_invalidated_attestations} testId="tile-revoked" icon={AlertTriangle} accent="text-rose-600" />
          <Tile label="Pending email verification" value={summary.mutual_pending_email_verification} testId="tile-pending-email" icon={Users} accent="text-amber-600" />
          <Tile label="Acceptance test records" value={summary.acceptance_test_records} testId="tile-test-records" icon={FlaskConical} accent="text-amber-600" />
        </div>
      )}

      <Tabs defaultValue="declarations" className="mt-8">
        <TabsList>
          <TabsTrigger value="declarations" data-testid="tab-declarations">Declarations</TabsTrigger>
          <TabsTrigger value="flags" data-testid="tab-flags">Review Signals</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="test-data" data-testid="tab-test-data">Test Data</TabsTrigger>
        </TabsList>

        <TabsContent value="declarations">
          <div className="flex items-center gap-3 mt-4 mb-3">
            <Label className="text-xs text-muted-foreground">Filter by status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-56 bg-white" data-testid="witness-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Declaration</TableHead>
                    <TableHead>Declarant</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Witnesses</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Locked</TableHead>
                    <TableHead>Ward</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="witness-declarations-table">
                  {rows.map((d) => {
                    const meta = STATUS_META[d.witness_status] || STATUS_META.self_declared;
                    return (
                      <TableRow key={d.id}>
                        <TableCell>
                          <p className="font-mono text-xs font-semibold">{d.declaration_number}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">{d.public_verification_id}</p>
                          {d.acceptance_test && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-[9px] mt-1" data-testid={`test-badge-${d.declaration_number}`}>
                              {d.test_label || "ACCEPTANCE TEST DATA"}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{d.voter?.full_name}</TableCell>
                        <TableCell><Badge variant="outline" className={`${meta.cls} text-[10px]`}>{meta.label}</Badge></TableCell>
                        <TableCell className="tabular-nums text-sm">{d.witness_count ?? 0}</TableCell>
                        <TableCell className="tabular-nums text-sm">v{d.content_version ?? 1}</TableCell>
                        <TableCell>{d.locked ? <Lock className="h-3.5 w-3.5 text-emerald-700" /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="text-xs">{d.ward || "—"}</TableCell>
                        <TableCell><AuditDialog declaration={d} /></TableCell>
                      </TableRow>
                    );
                  })}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">No declarations match this filter.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="flags">
          <Card className="mt-4">
            <CardContent className="p-5">
              {!flags ? <Skeleton className="h-32" /> : (
                <>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-4" data-testid="flags-note">{flags.note}</p>
                  <ul className="space-y-2.5" data-testid="witness-flags-list">
                    {flags.flags.map((f, i) => (
                      <li key={i} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3.5 flex items-start gap-3">
                        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold uppercase tracking-wider text-amber-800">{f.kind.replace(/_/g, " ")}</span>
                            <Badge variant="outline" className="text-[9px] bg-white">{f.severity}</Badge>
                          </div>
                          <p className="text-sm text-foreground mt-1">{f.detail}</p>
                        </div>
                      </li>
                    ))}
                    {flags.flags.length === 0 && <p className="text-sm text-muted-foreground">No review signals at this time.</p>}
                  </ul>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card className="mt-4 max-w-xl">
            <CardContent className="p-6">
              <h2 className="font-heading font-semibold text-base text-foreground mb-1 inline-flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-primary" /> Witness settings
              </h2>
              <p className="text-xs text-muted-foreground mb-5">These controls apply platform-wide.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="req-count" className="text-xs">Required witnesses (0–2)</Label>
                  <Select value={settings.required_witness_count} onValueChange={(v) => setSettings({ ...settings, required_witness_count: v })}>
                    <SelectTrigger id="req-count" className="mt-1 bg-white" data-testid="setting-required-witnesses"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="0">0 — self-declared only</SelectItem>
                      <SelectItem value="1">1 witness (default)</SelectItem>
                      <SelectItem value="2">2 witnesses</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="agg-threshold" className="text-xs">Public aggregate minimum threshold</Label>
                  <Input id="agg-threshold" type="number" min="1" className="mt-1" value={settings.aggregate_min_threshold}
                         onChange={(e) => setSettings({ ...settings, aggregate_min_threshold: e.target.value })}
                         data-testid="setting-aggregate-threshold" />
                  <p className="text-[10px] text-muted-foreground mt-1">Public stats appear only when a group (including each ward) has at least this many declarations.</p>
                </div>
              </div>
              <Button className="mt-5" onClick={saveSettings} data-testid="save-witness-settings">Save settings</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="test-data">
          <Card className="mt-4">
            <CardContent className="p-6">
              <h2 className="font-heading font-semibold text-base text-foreground mb-1 inline-flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-amber-600" /> Acceptance test data
              </h2>
              <p className="text-xs text-muted-foreground mb-4" data-testid="test-data-note">
                {testData?.note || "Declarations made with @test.example emails are marked ACCEPTANCE TEST DATA and excluded from all public statistics, totals and alerts."}
              </p>
              {testData && (
                <>
                  <div className="space-y-2" data-testid="test-data-list">
                    {testData.declarations.map((d) => (
                      <div key={d.id} className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="font-mono text-xs font-semibold">{d.declaration_number} <span className="text-muted-foreground">· {d.public_verification_id}</span></p>
                          <p className="text-xs text-muted-foreground">{d.voter?.full_name} · {d.voter?.email_lower} · {d.witness_status} · v{d.content_version}</p>
                        </div>
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-[9px]">{d.test_label || "ACCEPTANCE TEST DATA"}</Badge>
                      </div>
                    ))}
                    {testData.declarations.length === 0 && <p className="text-sm text-muted-foreground">No acceptance test declarations exist.</p>}
                  </div>
                  {testData.declarations.length > 0 && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="mt-5 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="purge-test-data-btn">
                          <Trash2 className="h-4 w-4 mr-1.5" /> Remove all acceptance test data
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove acceptance test data?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently removes all test declarations, witness records, participants and related
                            drafts created with @test.example emails. Real declarations are never touched.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="purge-cancel">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={purgeTestData} className="bg-rose-600 hover:bg-rose-700" data-testid="purge-confirm">
                            Remove test data
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
